// The Faculty directory, department rosters, and the faculty detail page all
// read the SAME merged view of who's faculty and what to call them: published
// content wins wherever it covers a person, staff.ts fills every gap
// published content hasn't reached yet.
//
// Why a merge and not a straight switch to published content: as of
// 2026-09-17, published content has ZERO faculty records for Physics,
// Chemistry, Maths, Humanities or MBA — a straight switch would make every
// person in those departments vanish from the site until webadmin catches
// up. A merge means nobody disappears; a department's listing upgrades to
// published data the moment webadmin publishes it, department by department,
// with no code change needed here.
//
// What "published wins" means field by field, for anyone matched by slug:
//   name, designation   -> published (designationLabel is the display string)
//   department           -> published department.code, run through the same
//                            resolveDepartment() the rest of the app already
//                            uses for legacy codes (CSE/ECE/EEE/ISE/IOT etc.),
//                            so this doesn't duplicate that mapping
//   teaching category     -> true. Published content has no explicit
//                            teaching/non-teaching flag, but "this person is
//                            in the faculty module" is itself webadmin's own
//                            categorization signal — including the ~27 people
//                            staff.ts currently files as non-teaching
//                            ("Instructor" / "Assistant Instructor" roles)
//   photo                 -> the already-reprocessed key from
//                            publishedFacultyPhotos.ts (never the raw
//                            external webadmin URL)
// Anyone NOT matched (not yet published) keeps every staff.ts field exactly
// as before.
//
// Order: the list starts in staff.ts order, then is sorted by webadmin's
// display order (see facultyOrder.ts). That number restarts at 1 in every
// department, so each department's own people come out in their display order
// and equal numbers in different departments simply tie. Anyone with no display
// order — not published, or no number on the record — comes after everyone who
// has one, in staff.ts order.
//
// 4 published faculty (as of 2026-09-17) are published under a slug that
// doesn't match any staff.ts record directly — poornima-b, shwetha-s,
// shwetha-s-2, pooja-k-n. All 4 are hand-verified duplicates of existing
// staff.ts people under a different slug convention, not new people; see
// publishedFacultySlugAliases.ts for exactly which staff.ts record each maps
// to and how it was checked (full name + designation + department, not name
// alone — one of the four shares its name with a completely different
// person elsewhere in staff.ts).
//
// A published faculty member with NO staff.ts counterpart at all (checked by
// normalized slug, same as above — not by name, since a name can collide
// with a different person) is a genuinely new hire, not a duplicate to
// reconcile: added rather than dropped, and sorted by display order like
// everyone else published. Found 2026-09-24: roopa-r
// and geetha-m, two new AI department faculty published with no photo yet —
// `photo` is optional on MergedStaffMember for exactly this case, so they
// get the same initials fallback every other photo-less card already uses
// instead of staying invisible until a photo exists.
import { useMemo } from "react";
import { STAFF, computeStaffDepts } from "./staff";
import type { StaffMember, StaffDept } from "./staff";
import type { Media } from "@zivin-labs/site-content";
import { resolveDepartment, type Department } from "./departments";
import { useCollection } from "./siteContent";
import { usePublishedFacultyPhotos } from "./publishedFacultyPhotos";
import { normalizePublishedSlug } from "./publishedFacultySlugAliases";
import { sortByDisplayOrder } from "./facultyOrder";

export interface MergedStaffMember extends Omit<StaffMember, "photo"> {
  photo?: Media;
  /** True when name/designation/department/category came from published content, not staff.ts. */
  isPublished: boolean;
  /** webadmin's display order for this person (it restarts at 1 in every department). Only set for people with a published record. */
  displayOrder?: number;
}

interface PublishedRecordShape {
  slug: string;
  name: string;
  /** webadmin's display order within the person's department — every department runs 1…n. */
  order?: number;
  /** Free-text override — present mainly for designation "other" (e.g. "Foreman"). */
  designationLabel?: string;
  /** Raw enum value (e.g. "associate_professor") — set on nearly every record; designationLabel usually isn't. */
  designation?: string;
  department: { code: string; name: string; slug: string } | null;
}

/** "associate_professor" -> "Associate Professor" when there's no dedicated designationLabel. */
function humanizeDesignation(pub: PublishedRecordShape, fallback: string): string {
  if (pub.designationLabel?.trim()) return pub.designationLabel.trim();
  if (pub.designation?.trim()) {
    return pub.designation
      .replace(/_/g, " ")
      .replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());
  }
  return fallback;
}

export interface MergedFaculty {
  list: MergedStaffMember[];
  depts: StaffDept[];
}

export function useMergedFaculty(): MergedFaculty {
  const { items } = useCollection("faculty");
  const publishedPhotos = usePublishedFacultyPhotos();

  return useMemo(() => {
    const records = items as unknown as PublishedRecordShape[];
    const bySlug = new Map(records.map((r) => [normalizePublishedSlug(r.slug), r]));

    const matchedSlugs = new Set<string>();
    const list: MergedStaffMember[] = STAFF.map((s) => {
      const pub = bySlug.get(s.slug);
      const photo = publishedPhotos.get(s.slug) ?? s.photo;
      if (!pub) return { ...s, photo, isPublished: false };

      matchedSlugs.add(s.slug);
      const dept = pub.department ? resolveDepartment(pub.department.code) : undefined;
      return {
        ...s,
        name: pub.name || s.name,
        designation: humanizeDesignation(pub, s.designation),
        deptCode: dept?.staffCode ?? dept?.code ?? s.deptCode,
        deptName: dept?.name ?? s.deptName,
        teaching: true,
        photo,
        isPublished: true,
        displayOrder: pub.order,
      };
    });

    // Published people with no staff.ts record at all — real new hires, not
    // duplicates (see the module comment above).
    for (const [slug, pub] of bySlug) {
      if (matchedSlugs.has(slug) || !pub.name?.trim()) continue;
      const dept = pub.department ? resolveDepartment(pub.department.code) : undefined;
      list.push({
        slug,
        name: pub.name.trim(),
        designation: humanizeDesignation(pub, ""),
        deptCode: dept?.staffCode ?? dept?.code ?? pub.department?.code ?? "",
        deptName: dept?.name ?? pub.department?.name ?? "",
        teaching: true,
        photo: publishedPhotos.get(slug),
        isPublished: true,
        displayOrder: pub.order,
      });
    }

    // Until here the list is in staff.ts order, with published-only people at
    // the end. The department filter list is built from that order, not the
    // sorted one: equally-sized departments tie on where they first appear, and
    // display order shouldn't reshuffle them.
    return { list: sortByDisplayOrder(list), depts: computeStaffDepts(list) };
  }, [items, publishedPhotos]);
}

/**
 * How many people a department's page lists: the merged faculty list, matched
 * on the department's staff code (its own code where it has none) — the same
 * rule the department page's roster uses, so a listing card can't disagree
 * with the page it links to.
 */
export function useDepartmentHeadcount(): (d: Department) => number {
  const { list } = useMergedFaculty();
  return (d) => {
    const code = (d.staffCode ?? d.code).toUpperCase();
    return list.filter((s) => s.deptCode.toUpperCase() === code).length;
  };
}
